import { useRef, useState } from "react";
import { FaCheck } from "react-icons/fa";
import { useNavigate } from "react-router";

import { openLink } from "@tma.js/sdk-react";

import {
  buildScopes,
  getPermissions,
  getScopes,
  type PermissionType,
  type PermissionValue,
} from "entities/permissions";
import {
  AppPasswordSection,
  isAppPasswordValid,
  normalizeAppPassword,
} from "features/app-password";
import { Layout } from "pages/ui/page";
import { useAuthRedirectLinkMutation } from "shared/api/use-auth-redirect-link-mutation";
import { type Account } from "shared/api/use-connected-accounts-query";
import { useDeleteAccountMutation } from "shared/api/use-delete-account-mutation";
import { useEditAccountMutation } from "shared/api/use-edit-account-mutation";
import { Button } from "shared/ui/button";
import { GoogleButton } from "shared/ui/google-button";
import { Notification } from "shared/ui/notification";
import { cn } from "shared/utils/cn";
import { InstructionsField } from "widgets/instructions-field";
import { Permissions } from "widgets/permissions";

type Props = {
  account: Account;
};

const ACCESS_LEVEL: Record<PermissionValue, number> = {
  "No Access": 0,
  "Read Only": 1,
  "Full Access": 2,
};

export const Content = (props: Props) => {
  const { account } = props;

  const navigate = useNavigate();
  const [instructions, setInstructions] = useState(account.instructions);
  const [password, setPassword] = useState("");
  const [passwordError, setPasswordError] = useState<string>();
  const authorizationRef = useRef<HTMLDivElement>(null);

  const initialPermissionsRef = useRef(
    getPermissions(account.scopes, account.type)
  );
  const [permissions, setPermissions] = useState<PermissionType[]>(
    initialPermissionsRef.current
  );

  const hasSavedPassword =
    "app_specific_password" in account.credentials &&
    Boolean(account.credentials.app_specific_password);

  const gmailPermission = permissions.find((p) => p.title === "Gmail");
  const isGmailEnabled =
    gmailPermission !== undefined && gmailPermission.value !== "No Access";
  const showAuthorization = account.type === "icloud" || isGmailEnabled;
  const isPasswordMissing =
    account.type === "google" &&
    isGmailEnabled &&
    !hasSavedPassword &&
    !isAppPasswordValid(password);

  // Reconnecting through Google is only required when the user grants MORE
  // permissions for a non-email (non-Gmail) scope. Reducing access or changing
  // the Gmail permission can be saved without going through Google again.
  const hasElevatedNonGmailPermission = permissions.some((permission) => {
    if (permission.title === "Gmail") return false;
    const initial = initialPermissionsRef.current.find(
      (p) => p.title === permission.title
    );
    if (!initial) return false;
    return ACCESS_LEVEL[permission.value] > ACCESS_LEVEL[initial.value];
  });

  const isGoogleReconnectRequired =
    account.is_outdated || hasElevatedNonGmailPermission;

  const hasChangedPermission = permissions.some((permission) => {
    const initial = initialPermissionsRef.current.find(
      (p) => p.title === permission.title
    );
    return initial !== undefined && initial.value !== permission.value;
  });

  const isDirty =
    instructions !== account.instructions ||
    password !== "" ||
    hasChangedPermission;

  const deleteAccountMutation = useDeleteAccountMutation();
  const authRedirectLinkMutation = useAuthRedirectLinkMutation();
  const editMutation = useEditAccountMutation();

  const handlePermissionValueChanged = (
    permission: PermissionType,
    newValue: PermissionValue
  ) => {
    setPermissions((permissions) => {
      return permissions.map((p) => {
        if (p.title === permission.title) {
          return { ...p, value: newValue };
        }
        return p;
      });
    });
  };

  const handleSave = async () => {
    if (isPasswordMissing) {
      setPasswordError("Enter an app password to enable Gmail");
      authorizationRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      return;
    }

    try {
      if (account.type === "google" && isGoogleReconnectRequired) {
        // Gmail is excluded: its synthetic scopes are not valid OAuth scopes
        // and gmail access is granted via the app password instead.
        const url = await authRedirectLinkMutation.mutateAsync({
          instructions,
          label: account.label ?? "",
          scopes: getScopes(
            permissions.filter((p) => p.title !== "Gmail"),
            "google"
          ),
        });
        openLink(url, {
          tryInstantView: false,
        });
        return;
      }

      await editMutation.mutateAsync({
        type: account.type,
        email: account.name,
        instructions,
        scopes: buildScopes(permissions, account.type, account.scopes),
        app_specific_password: password
          ? normalizeAppPassword(password)
          : undefined,
      });
      navigate("/accounts");
    } catch (error) {
      console.error(error);
      if (password) {
        setPasswordError("Password is not valid for this account");
        authorizationRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
    }
  };

  const handleRemove = async () => {
    try {
      await deleteAccountMutation.mutateAsync({
        name: account.name,
        type: account.type,
      });

      navigate("/accounts");
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <Layout>
      {account.is_outdated && (
        <Notification className="mb-5" type="danger">
          {account.type === "google" ? "Google" : "iCloud"} account connection
          has expired. Please reconnect to continue using it with Resonar AI.
        </Notification>
      )}

      <h1 className="flex text-lg leading-5.5 font-medium tracking-[0.4px] text-white">
        {account.name}
      </h1>

      <Permissions
        className="mt-5"
        permissions={permissions}
        onChange={handlePermissionValueChanged}
      />

      {showAuthorization && (
        <div ref={authorizationRef}>
          <AppPasswordSection
            className="mt-5"
            description={
              account.type === "icloud"
                ? "An app-specific password lets Resonar securely access your Apple calendar, contacts and mail. It is not your Apple ID password."
                : "An app password lets Resonar securely read and send your Gmail. It is not your Google account password."
            }
            learnMoreUrl={
              account.type === "icloud"
                ? "https://support.apple.com/102654"
                : "https://support.google.com/accounts/answer/185833"
            }
            value={password}
            error={passwordError}
            hasSavedPassword={hasSavedPassword}
            onChange={(value) => {
              setPasswordError(undefined);
              setPassword(value);
            }}
          />
        </div>
      )}

      <InstructionsField
        className="mt-5 mb-3"
        instructions={instructions}
        onChange={setInstructions}
      />

      <div className="mt-auto flex flex-col gap-y-2 pt-4">
        {account.type === "google" && isGoogleReconnectRequired && (
          <GoogleButton
            onClick={handleSave}
            isLoading={authRedirectLinkMutation.isPending}
          >
            {account.is_outdated ? "Reconnect account" : "Continue with Google"}
          </GoogleButton>
        )}
        {!(account.type === "google" && isGoogleReconnectRequired) && (
          <Button
            theme="white"
            size="52"
            className={cn(isPasswordMissing && "opacity-50")}
            disabled={!isDirty}
            isLoading={editMutation.isPending}
            onClick={handleSave}
          >
            {editMutation.isSuccess ? (
              <FaCheck className="size-5" />
            ) : (
              "Save changes"
            )}
          </Button>
        )}
        <Button
          isLoading={deleteAccountMutation.isPending}
          className="text-pale-red"
          theme="text"
          onClick={handleRemove}
        >
          Remove Account
        </Button>
      </div>
    </Layout>
  );
};
